import { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@gto/ui/button'
import { Switch } from '@gto/ui/switch'
import { Typography } from '@gto/ui/typography'

import { AppDialog } from '@/components/app-dialog'
import {
  type AffiliateAdminCouponSchema,
  type AffiliateUpdateCouponSchema,
  buildCouponUpdateRequest,
  couponEditFormSchema,
  type CouponEditFormValues,
  type CouponPlan,
  inferPlanScope,
  type PlanScope,
  productKey,
  resolveSubmitPlanIds,
} from '@/domain/coupons'

import { ControlledNumberField } from './controlled-number-field'
import { ControlledTextField } from './controlled-text-field'
import { CouponFormError } from './coupon-form-error'
import { CouponSharedFields, type CouponTagsState } from './coupon-shared-fields'
import { CouponSubmitButton } from './coupon-submit-button'
import { useCouponCodeServerError } from './use-coupon-code-server-error'

// Map a reconstructed scope onto the flat scope form fields.
const scopeToFormFields = (scope: PlanScope) => {
  const empty: {
    scopePlanId: string
    scopeTier: string
    scopeProduct: string
    scopeInterval: string
    customPlanIds: number[]
  } = {
    scopePlanId: '',
    scopeTier: '',
    scopeProduct: '',
    scopeInterval: '',
    customPlanIds: [],
  }
  switch (scope.kind) {
    case 'all':
      return { scopeKind: 'all' as const, ...empty }
    case 'specific':
      return { scopeKind: 'specific' as const, ...empty, scopePlanId: String(scope.planId) }
    case 'tier':
      return {
        scopeKind: 'tier' as const,
        ...empty,
        scopeTier: scope.tier,
        scopeInterval: scope.interval,
      }
    case 'product':
      return {
        scopeKind: 'product' as const,
        ...empty,
        scopeProduct: productKey({ variant: scope.variant, type: scope.type }),
        scopeInterval: scope.interval,
      }
    case 'custom':
      return { scopeKind: 'custom' as const, ...empty, customPlanIds: scope.planIds }
  }
}

const buildInitialValues = (
  coupon: AffiliateAdminCouponSchema,
  plans: CouponPlan[],
): CouponEditFormValues => ({
  code: coupon.code,
  tagIds: coupon.tags.map((tag) => tag.id),
  discountValue: String(coupon.discount_value),
  // A stored null means "unlimited" (max_uses) / "never expires" (days_valid);
  // represent it as an empty field so the value round-trips faithfully. Every
  // coupon from the old create flow has both null — fabricating '1' / '365'
  // here would silently cap uses at 1 and stamp a 365-day expiry on the next
  // save (e.g. editing only the discount).
  maxUses: coupon.max_uses == null ? '' : String(coupon.max_uses),
  daysValid: coupon.days_valid == null ? '' : String(coupon.days_valid),
  isOneUsePerUser: coupon.is_one_use_per_user,
  firstPaymentOnly: coupon.first_payment_only,
  isActive: coupon.is_active,
  ...scopeToFormFields(inferPlanScope(coupon.plan_ids, plans)),
})

type CouponEditDialogProps = {
  coupon: AffiliateAdminCouponSchema
  tagState: CouponTagsState
  plans: CouponPlan[]
  plansLoading: boolean
  plansError: boolean
  submitting: boolean
  /** Latest update mutation error — a 422 on `code` maps to the field. */
  serverError: unknown
  onClose: () => void
  onSubmit: (body: AffiliateUpdateCouponSchema) => void
}

export const CouponEditDialog = ({
  coupon,
  tagState,
  plans,
  plansLoading,
  plansError,
  submitting,
  serverError,
  onClose,
  onSubmit,
}: CouponEditDialogProps) => {
  const { formatMessage } = useIntl()
  const form = useForm<CouponEditFormValues>({
    resolver: zodResolver(couponEditFormSchema),
    defaultValues: buildInitialValues(coupon, plans),
  })
  useCouponCodeServerError(serverError, (message) =>
    form.setError('code', { type: 'server', message }),
  )
  const availableTags = [
    ...coupon.tags,
    ...tagState.tags.filter(
      (catalogTag) => !coupon.tags.some((selectedTag) => selectedTag.id === catalogTag.id),
    ),
  ]

  // The initial defaults are built before the plan catalog arrives, so a
  // tier/product span under-resolves to `custom`. Re-derive once the catalog
  // loads — but only while the form is pristine, so an in-progress edit is never
  // clobbered.
  useEffect(
    function reconstructScopeOnPlansLoaded() {
      if (!form.formState.isDirty) {
        form.reset(buildInitialValues(coupon, plans))
      }
    },
    [coupon, plans, form],
  )

  const submit = form.handleSubmit((values) => {
    // `null` = the selection must not be submitted (incomplete scope, or a
    // no-match scoped selection that would send plan_ids: [] = all plans). The
    // CTA is already disabled in that state; this is the belt-and-suspenders
    // guard.
    const planIds = resolveSubmitPlanIds(values, plans)
    if (planIds === null) {
      return
    }
    onSubmit(buildCouponUpdateRequest(values, planIds))
  })

  return (
    <AppDialog
      open
      width={560}
      onClose={onClose}
    >
      <AppDialog.Header title={formatMessage({ id: 'admin.partnerDetail.coupons.edit' })} />
      <FormProvider {...form}>
        <AppDialog.Body>
          <CouponFormError serverError={serverError} />
          <ControlledTextField
            control={form.control}
            data-tst="coupon-code"
            label={formatMessage({ id: 'admin.partnerDetail.coupons.code' })}
            maxLength={64}
            name="code"
          />
          <CouponSharedFields
            plans={plans}
            plansError={plansError}
            plansLoading={plansLoading}
            tagState={{ ...tagState, tags: availableTags }}
            maxUsesSlot={
              <ControlledNumberField
                control={form.control}
                helperText={formatMessage({ id: 'coupon.maxUsesHelper' })}
                label={formatMessage({ id: 'coupon.maxUses' })}
                min={1}
                name="maxUses"
              />
            }
          />
          <div
            className="flex flex-row items-start justify-between gap-3"
            data-tst="coupon-auto-renewal-status"
          >
            <div className="flex flex-col">
              <Typography variant="body2">{formatMessage({ id: 'coupon.autoRenewal' })}</Typography>
              <Typography
                color="text.secondary"
                variant="caption"
              >
                {formatMessage({ id: 'coupon.autoRenewalReadOnly' })}
              </Typography>
            </div>
            <Typography variant="body2">
              {formatMessage({
                id: coupon.auto_cancelled ? 'coupon.autoRenewalOff' : 'coupon.autoRenewalOn',
              })}
            </Typography>
          </div>
          <Controller
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <div className="flex flex-row items-center justify-between gap-3">
                <Typography variant="body2">{formatMessage({ id: 'coupon.active' })}</Typography>
                <Switch
                  aria-label={formatMessage({ id: 'coupon.active' })}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </div>
            )}
          />
        </AppDialog.Body>
        <AppDialog.Footer>
          <Button onClick={onClose}>
            <FormattedMessage id="admin.common.cancel" />
          </Button>
          <CouponSubmitButton
            data-tst="coupon-submit-edit"
            label={<FormattedMessage id="admin.common.save" />}
            plans={plans}
            submitting={submitting || tagState.creatingTag}
            onClick={() => void submit()}
          />
        </AppDialog.Footer>
      </FormProvider>
    </AppDialog>
  )
}
